import { supabase } from './supabaseClient';

/**
 * Generate a URL-friendly slug from a title. Not guaranteed unique on its
 * own — callers append a short suffix on collision (see createPost).
 */
export function slugify(title) {
  return (title || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'post';
}

function mapPost(row) {
  if (!row) return null;
  return {
    id: row.id,
    title: row.title,
    slug: row.slug,
    excerpt: row.excerpt || '',
    content: row.content || '',
    coverImageUrl: row.cover_image_url || null,
    status: row.status,
    views: row.views || 0,
    authorName: row.author_name || '',
    publishedAt: row.published_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/** Public: published posts only, newest first. */
export async function getPublishedPosts() {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('status', 'published')
    .order('published_at', { ascending: false });

  if (error) {
    console.error('Error fetching published posts:', error);
    throw error;
  }
  return (data || []).map(mapPost);
}

/** Public: single published post by slug. */
export async function getPostBySlug(slug) {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle();

  if (error) {
    console.error('Error fetching post by slug:', error);
    throw error;
  }
  return mapPost(data);
}

/** Public: bump a published post's view counter by one (fire-and-forget). */
export async function incrementPostViews(slug) {
  const { error } = await supabase.rpc('increment_blog_post_views', { post_slug: slug });
  if (error) {
    console.error('Error incrementing post views:', error);
  }
}

/** Admin: every post regardless of status, newest first. */
export async function getAllPostsForAdmin() {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching posts for admin:', error);
    throw error;
  }
  return (data || []).map(mapPost);
}

/** Admin: single post by id, any status (for editing). */
export async function getPostById(id) {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching post by id:', error);
    throw error;
  }
  return mapPost(data);
}

async function slugExists(slug, excludeId) {
  let query = supabase.from('blog_posts').select('id').eq('slug', slug);
  if (excludeId) query = query.neq('id', excludeId);
  const { data, error } = await query.maybeSingle();
  if (error) return false;
  return !!data;
}

/** Ensure a unique slug, appending -2, -3, ... on collision. */
async function uniqueSlug(baseSlug, excludeId) {
  let candidate = slugify(baseSlug);
  let suffix = 2;
  while (await slugExists(candidate, excludeId)) {
    candidate = `${slugify(baseSlug)}-${suffix}`;
    suffix += 1;
  }
  return candidate;
}

/**
 * Create a post. `data` = { title, slug, excerpt, content, coverImageUrl,
 * status, authorName }. If slug is omitted it's derived from the title.
 */
export async function createPost(data) {
  const slug = await uniqueSlug(data.slug || data.title);
  const status = data.status === 'published' ? 'published' : 'draft';
  const payload = {
    title: data.title,
    slug,
    excerpt: data.excerpt || '',
    content: data.content || '',
    cover_image_url: data.coverImageUrl || null,
    status,
    author_name: data.authorName || '',
    published_at: status === 'published' ? new Date().toISOString() : null,
  };

  const { data: row, error } = await supabase
    .from('blog_posts')
    .insert(payload)
    .select('*')
    .single();

  if (error) {
    console.error('Error creating post:', error);
    throw error;
  }
  return mapPost(row);
}

/**
 * Update a post. `data` may include any of the same fields as createPost.
 * If the title changed and no explicit slug was passed, the slug is left
 * alone — editing a published post's title shouldn't silently break its URL.
 */
export async function updatePost(id, data) {
  const payload = { updated_at: new Date().toISOString() };
  if (data.title !== undefined) payload.title = data.title;
  if (data.excerpt !== undefined) payload.excerpt = data.excerpt;
  if (data.content !== undefined) payload.content = data.content;
  if (data.coverImageUrl !== undefined) payload.cover_image_url = data.coverImageUrl;
  if (data.authorName !== undefined) payload.author_name = data.authorName;
  if (data.slug !== undefined) payload.slug = await uniqueSlug(data.slug, id);

  if (data.status !== undefined) {
    payload.status = data.status === 'published' ? 'published' : 'draft';
    if (payload.status === 'published' && data.setPublishedAt !== false) {
      payload.published_at = new Date().toISOString();
    }
    if (payload.status === 'draft' && data.clearPublishedAt) {
      payload.published_at = null;
    }
  }

  const { data: row, error } = await supabase
    .from('blog_posts')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    console.error('Error updating post:', error);
    throw error;
  }
  return mapPost(row);
}

export async function deletePost(id) {
  const { error } = await supabase.from('blog_posts').delete().eq('id', id);
  if (error) {
    console.error('Error deleting post:', error);
    throw error;
  }
  return true;
}

export async function publishPost(id) {
  return updatePost(id, { status: 'published' });
}

export async function unpublishPost(id) {
  return updatePost(id, { status: 'draft', clearPublishedAt: false });
}
